import DesignTokens from '@tableau/design-tokens';
import { CloseButtonWidget } from '@tableau/widgets';
import React from 'react';
import _ from 'underscore';

import { Messages } from '~src/codegen/Localize';
import { CommonStyles } from '~src/commonInfra/styles/CommonStyles';
import { DeviceType } from '~src/devicePreview/models/DeviceType';
import { DevicePreviewService } from '~src/devicePreview/services/DevicePreviewService';
import { ResourceQuery } from '~src/httpClient/resources/ResourceQuery';
import { getSessionManagementServiceInstance, SessionManagementService } from '~src/sessionManagement/SessionManagementService';
import { Order } from '~src/spacesPlatform/models/sort/Order';
import { applyColor }from '~src/style/ColorService';
import { IViewDetailInfo, IViewInfo } from '~src/views/models/IViewInfo';
import { Views } from '~src/views/resources/Views';
import { VizProps, VizView } from '~src/viz/components/Viz';
import { VizType } from '~src/viz/models/Viz';
import { VizLoadContextService, VizLoadType } from '~src/viz/services/VizLoadContextService';
import { VizService } from '~src/viz/services/VizService';
import { vizStyle } from '~src/viz/styles/VizViewerStyles';

import { DevicePickerBar } from './DevicePickerBar';

export interface DevicePreviewModalProps {
  readonly vizPath: string;
  readonly dismiss: () => void;
}

interface DevicePreviewModalState {
  readonly vizPath: string;
  readonly devicePickerBarProps: DevicePickerBar.Props;
  readonly sessionManagementService: SessionManagementService;
}

export class DevicePreviewModal extends React.PureComponent<DevicePreviewModalProps, DevicePreviewModalState> {

  private vizContainerRef = React.createRef<HTMLDivElement>();
  private viz: tb.IViz = undefined; // the actual viz object
  private vizProps: VizProps = undefined;
  private server = getSessionManagementServiceInstance();

  public constructor(props: DevicePreviewModalProps) {
    super(props);
    this.state = {
      vizPath: props.vizPath,
      sessionManagementService: getSessionManagementServiceInstance(),
      devicePickerBarProps: {
        onDeviceTypeClick: (device: DeviceType) => {
          const previewDevice: DeviceType = DevicePreviewService.changePreviewDevice(device);
          if (previewDevice) {
            this.handleDeviceChange(previewDevice);
          }
        },
        selectedDevice: DevicePreviewService.getPreviewDevice(),
      }
    };
  }

  public componentDidMount() {
    this.initializeNavigationToolsProps();
    // Set viz load type to DevicePreviewOpen when the device preview modal is opened
    VizLoadContextService.set(VizLoadType.DevicePreviewOpen);
    this.loadViz();
  }

  private loadViz = () => {
    if (this.vizContainerRef.current) {
      this.viz && this.viz.dispose();
      this.viz = VizView.loadViz(this.vizProps || this.initVizViewerProps(), this.vizContainerRef.current);
    }
  };
  private initVizViewerProps = (): VizProps => {
    const props = {} as VizProps;
    const vizService = new VizService();

    props.viz = {
      path: this.state.vizPath,
      vizType: VizType.DeviceVizPreview,
      params: vizService.getVizParams(),
      siteUrlName: this.server.sessionSiteUrlName(),
      instanceId: vizService.getInstanceId(),
      isExplicitOriginalViz: vizService.getIsExplicitOriginalViz(),
    };
    // setting these as noop to avoid errors when tableau.viz tries to access them
    props.dalert = undefined;
    props.onVizInstanceIdChange = _.noop;
    props.onVizPathChange = _.noop;
    props.onVizIsExplicitOriginalVizChange = (isExplicitOriginalViz: boolean) => {
      vizService.updateExplicitOriginalViz(isExplicitOriginalViz);
    };
    props.clearDataAlertRoute = _.noop;

    this.vizProps = props;
    return props;
  };

  public componentWillUnmount() {
    this.viz && this.viz.dispose();
  }

  private getViewCountWrapper(viewsQuery: ResourceQuery<IViewInfo>) {
    return viewsQuery.getCount();
  }

  private getViewSliceWrapper(viewsQuery: ResourceQuery<IViewInfo>, start: number, size: number) {
    return viewsQuery.getSlice(start, size);
  }

  private initializeNavigationToolsProps() {
    Views.getByPath(this.props.vizPath)
    .then((viewInfo: IViewDetailInfo) => {
      const orderClause = [ Order.asc('index') ];
      const viewsQuery = Views.forWorkbook(viewInfo.workbook).withOrder(orderClause);

      const viewsForWorkbook = this.getViewCountWrapper(viewsQuery)
      .then((viewCount) => this.getViewSliceWrapper(viewsQuery, 0, viewCount))
      .then((result) => result.items);

      return Promise.all([
        viewsForWorkbook,
        viewInfo.id
      ]);
    })
    .then((values: any[]) => {
      const viewsForWorkbook = values[0];
      const currentViewId = values[1];
      this.setState({
        devicePickerBarProps: _.extend({}, this.state.devicePickerBarProps, {
          navigationToolsProps: this.buildNavigationToolsProps(viewsForWorkbook, currentViewId)
        })
      });
    });
  }

  // public for testing purposes only
  public buildNavigationToolsProps(viewList: IViewInfo[], currentViewId: string) {
    const indexOfCurrentView = viewList.findIndex(view => view.id === currentViewId);

    return {
      viewList: viewList,
      startingViewIndex: (indexOfCurrentView !== -1 ? indexOfCurrentView : 0),
      onNewViewSelected: (newView: IViewInfo) => {
        this.handleChangedPath(newView.path);
      }
    };
  }

  private handleDeviceChange(previewDevice: DeviceType) {
    this.setState({devicePickerBarProps: _.extend({}, this.state.devicePickerBarProps, {
      selectedDevice: previewDevice
    })}, () => {
      this.initVizViewerProps();
      // Set viz load type to DevicePreviewDeviceChange when the device is changed
      VizLoadContextService.set(VizLoadType.DevicePreviewDeviceChange);
      this.loadViz();
    });
  }

  private handleChangedPath(newPath: string) {
    this.setState({vizPath: newPath}, () => {
      this.initVizViewerProps();
      // Set viz load type to DevicePreviewViewChange when the view is changed
      VizLoadContextService.set(VizLoadType.DevicePreviewViewChange);
      this.loadViz();
    });
  }

  private closeButton(): JSX.Element {
    // TFS 840752: In IE11, it is necessary to supply a "height" style to the button in order to
    // have the flex CSS center the icon vertically. It actually doesn't matter what the "height"
    // value actually is, so long as it is smaller than the "min-height" (which is automatically
    // supplied by ButtonWidget).
    // See this for more info: https://stackoverflow.com/a/25386261
    const buttonStyle: React.CSSProperties = {
      float: 'right',
      cursor: 'pointer',
      height: 0,
      color: applyColor(DesignTokens.color.mode_agnostic.default.neutral90, CommonStyles.Colors.Grey230)
    };
    const testId = 'device-preview-close';
    const buttonWidgetProps = {
      handleClick: this.props.dismiss,
      testId: testId,
      style: buttonStyle,
      useDarkStyling: true,
    };

    return (
      <CloseButtonWidget {...buttonWidgetProps}/>
    );
  }

  private title(): JSX.Element {
    return (
      <div className="tb-preview-title-box">
        <h1>{Messages.previewDeviceLayouts()}</h1>
      </div>
    );
  }

  public render(): JSX.Element {
    return (
    <React.Fragment>
      {this.closeButton()}
      {this.title()}
      <div className="tb-device-picker-bar">
        <DevicePickerBar {...this.state.devicePickerBarProps} />
      </div>
      <div style={{ paddingTop: '24px' }} className="tb-fill tb-device-viz-preview-fill">
        <div ref={this.vizContainerRef} style={vizStyle} className={'tb-device-viz-preview tb-viz'} id={'viz'} tb-test-id={'viz'} data-tb-test-id="preview-viz" />
      </div>
    </React.Fragment>);
  }
}
